import React, { FunctionComponent } from "react";
import { Form } from "antd";
import FormLabel from "../FormLabel";
import ListLoadFailure from "./ListLoadFailure";
import styles from "../../BiformLayout.module.scss";
import { buildRules } from "../../formUtils";
import { QUESTION_TYPES } from "../../constants";
import { V2Question } from "../../helper";
import LocationInput from "../LocationInput";

/**
 * Location becomes a picker only on a short text question, the type Form Builder seeds; a location
 * marked on any other type renders as it was built, exactly as Company and Role do.
 */
export const usesLocationInput = (question?: V2Question): boolean => Boolean(question && question.questionType === QUESTION_TYPES.SHORT_TEXT);

interface LocationFieldProps {
  question: V2Question;
  locations: string[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  disabled?: boolean;
}

// The builder's default placeholder for a text question is "Enter", which reads wrong on a picker.
const resolvePlaceholder = (question: V2Question) =>
  question.placeholder && question.placeholder !== "Enter" ? question.placeholder : "Select or type your location";

/**
 * An off-campus request's Location question. The question's own label, required flag and length rules
 * apply to the name, which is what is submitted — there is no id to send alongside it.
 *
 * A failed list is named under the field, with what the learner can do instead, because the only
 * other sign of it would be a dropdown offering nothing but what they typed — which is exactly what
 * an unlisted place looks like when the list did load.
 */
const LocationField: FunctionComponent<LocationFieldProps> = ({ question, locations, loading, failed, onRetry, disabled = false }) => (
  <Form.Item
    name={question.id}
    label={<FormLabel label={question.label} required={question.isRequired} description={question.description} />}
    rules={buildRules(question)}
    extra={!disabled && failed ? <ListLoadFailure noun="location list" hint="You can still type your location." onRetry={onRetry} /> : undefined}
    // Read-only views show the saved answer like the other questions' read-only inputs, not greyed out.
    className={styles.disableSingleSelectInput}
  >
    <LocationInput
      disabled={disabled}
      locations={disabled ? [] : locations}
      loading={!disabled && loading}
      failed={!disabled && failed}
      onRetry={onRetry}
      allowClear={!question.isRequired}
      placeholder={resolvePlaceholder(question)}
    />
  </Form.Item>
);

export default LocationField;
